import {
    Box,
    BoxProps,
    Button,
    IconButton,
    List,
    styled,
    Switch,
    SxProps,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { IComment, IPolicySet } from '@tymely/atoms';
import { CommentHighlight, highlightHtml } from '@tymely/components/Comment';
import format from 'date-fns/format';
import React, { CSSProperties, ReactNode, RefObject, useEffect, useMemo, useState } from 'react';
import {
    useAppMode,
    useCommentArgumentsQuery,
    useCommentToggleIsCustomer,
    useEventSubscription,
    useResetComment,
    useTicketOrganization,
} from '@tymely/services';
import { useIsMutating } from '@tanstack/react-query';
import { ArrawIcon } from '@tymely/components/Menu/NavMenu';
import root from 'react-shadow';
import MarkdownIt from 'markdown-it';
import { MD_ID_INTENT } from '@tymely/features/Ticket/QaMode/constants';
import { useIntentsByIds } from '@tymely/services/hooks/comment.services';
import ModeIcon from '@tymely/components/Icons/Mode';

import ThinkingBubbleToggleButton from './ChatEvent/components/ThinkingBubbleToggleButton';
import Attachments from './Attachments';
import { getIntentIdsFromArg, linkRegexp, sanitizeHtml } from './utils';
import { BubbleContainer, CommentChip, CurrentLabel, InfoButton, InfoListItem } from './styled';

const IPHONE_SIGNATURE = 'Sent from my iPhone';

const tryFormatJson = (text: string): string | null => {
    const trimmed = text.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return null;
    }
    try {
        const parsed = JSON.parse(trimmed);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return null;
    }
};

const md = new MarkdownIt({
    breaks: true,
    linkify: true,
    typographer: true,
});

type CommentBubbleProps = BoxProps & {
    comment: IComment;
    highlightText?: string;
    intent?: IPolicySet;
    title?: string;
    withDate?: boolean;
    selected?: boolean;
    focusedOn?: boolean;
    showInfo?: boolean;
    htmlProps?: { ref?: RefObject<HTMLDivElement>; sx: SxProps };
    onUntag?: (comment: IComment) => void;
    children?: ReactNode;
    setShowConversationMessages?: React.Dispatch<React.SetStateAction<boolean>>;
    showConversationMessages?: boolean;
    onToggleConversationMessages?: () => void;
    thinkingContent?: string;
    onToggleThinking?: () => void;
};

const HTML_SX: SxProps = {
    position: 'relative',
    overflowWrap: 'anywhere',
    blockquote: { marginBlock: 0, marginInline: 0 },
    pre: { whiteSpace: 'break-spaces' },
};

const _Main = styled(Box)`
    display: flex;
`;

const _InfoList = styled(List)`
    border-left: 1px solid #dbdded;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    margin-left: ${({ theme }) => theme.spacing(4)};
`;

const _Details = styled(Box)`
    position: relative;
    width: 100%;
`;

const _Attachments = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const _AttachmentsContainer = styled(Box)`
    display: flex;
    align-items: center;
    margin-right: ${({ theme }) => theme.spacing(1)};
`;

const _AttachmentsLabel = styled(Typography)`
    margin-right: ${({ theme }) => theme.spacing(0.5)};
`;

const _InternalLabel = styled(Typography)`
    color: ${({ theme }) => theme.palette.warning.dark};
`;

const _Header = styled(Box)`
    display: flex;
    flex-direction: column;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const _SwitchContainer = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
    align-items: center;
    margin-top: ${({ theme }) => theme.spacing(-1)};
    margin-right: ${({ theme }) => theme.spacing(-1)};
`;

const CommentBubble = ({
    withDate,
    comment,
    selected,
    focusedOn,
    htmlProps,
    showInfo,
    onUntag,
    highlightText,
    intent,
    title,
    children,
    setShowConversationMessages,
    showConversationMessages,
    onToggleConversationMessages,
    onToggleThinking,
    thinkingContent,
    ...rest
}: CommentBubbleProps) => {
    const conversationData = comment?.conversation_data;
    const formattedJson = useMemo(() => tryFormatJson(comment.body || ''), [comment.body]);
    const html = comment.body_html || md.render(comment.body || '').replace(/\n+/g, '');
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return html.indexOf(IPHONE_SIGNATURE) !== -1;
    });
    const highlights = (comment.additional_data?.['highlights'] || []) as CommentHighlight[];
    const attachments = comment.attachment_links?.filter((link) => linkRegexp.test(link)) || [];

    const isBodyEmpty = useMemo(() => !html.trim(), [html]);

    const { visibleHtml, hasCollapsibleContent } = useMemo(() => {
        const signatureIndex = html.indexOf(IPHONE_SIGNATURE);

        if (signatureIndex === -1) {
            return {
                visibleHtml: html,
                hasCollapsibleContent: false,
            };
        }

        const beforeSignature = html.substring(0, signatureIndex);

        return {
            visibleHtml: beforeSignature,
            hasCollapsibleContent: true,
        };
    }, [html]);

    const bodyHtml = useMemo(() => {
        const highlightList = [...highlights];
        if (highlightText) {
            highlightList.push({
                token: highlightText,
                class: 'highlight-text',
                textMode: true,
                global: true,
            });
        }

        const htmlToProcess = isCollapsed && hasCollapsibleContent ? visibleHtml : html;
        const result = highlightHtml(highlightList, htmlToProcess);
        return comment.is_customer ? sanitizeHtml(result) : result;
    }, [comment, highlights, highlightText, html, attachments, isCollapsed, visibleHtml, hasCollapsibleContent]);

    const [infoOpen, setInfoOpen] = useState(showInfo || false);
    const [attachOpen, setAttachOpen] = useState(isBodyEmpty);
    const htmlSx: SxProps = { ...HTML_SX, ...htmlProps?.sx };

    const theme = useTheme();
    const htmlElem = (
        <Box
            ref={htmlProps?.ref}
            sx={htmlSx}
            className="content"
            style={htmlSx as CSSProperties}
            dangerouslySetInnerHTML={{
                __html:
                    bodyHtml +
                    `<style>
                        .content img { max-width: 800px; max-height: 800px;}
                        .content .highlight-text{background-color:${theme.palette.primary.main};}
                        .content table {border-collapse:collapse;}
                        .content a {text-decoration: underline !important; color: ${theme.palette.info.main} !important;}
                        .content pre {white-space: pre-wrap;}
                    </style>`,
            }}
        />
    );

    const { mutateAsync: resetComment } = useResetComment(comment);
    const isResettingComment = useIsMutating({ mutationKey: ['resetComment', comment?.id] }) > 0;

    const { isOnline } = useAppMode();
    const toggleIsCustomer = useCommentToggleIsCustomer();

    const disabled = comment.is_customer && isOnline && !focusedOn;

    const { data: args } = useCommentArgumentsQuery(comment?.id);
    const intentArg = args?.find(({ md_id }) => md_id === MD_ID_INTENT);
    const [intentIds, setIntentIds] = useState<number[]>(getIntentIdsFromArg(intentArg?.value));
    const organization = useTicketOrganization();
    useEffect(() => {
        setIntentIds(getIntentIdsFromArg(intentArg?.value));
    }, [JSON.stringify(intentArg?.value)]);

    useEventSubscription<{ intent_ids: number[] }>(
        { topic: `comment/${comment.id}`, filter: (event) => event.object_type === 'Comment' },
        (event) => {
            const newIntentIds = event.data.intent_ids;
            if (event.data && Array.isArray(newIntentIds)) {
                if (!organization?.config?.conversation?.enabled) {
                    setIntentIds(newIntentIds);
                } else {
                    setIntentIds((prevIntentIds) => [...new Set([...prevIntentIds, ...newIntentIds])]);
                }
            } else {
                setIntentIds(getIntentIdsFromArg(intentArg?.value));
            }
        },
        undefined,
        [comment.id, comment.ticket_id, organization],
    );
    const intentsFromIds = useIntentsByIds(intentIds);

    const intents = useMemo(() => {
        if (intentsFromIds?.length && organization?.config?.conversation?.enabled) {
            return intentsFromIds;
        }
        if (intent) {
            return [intent];
        }
        return [];
    }, [intentsFromIds, intent]);

    const bubbleContent = (
        <BubbleContainer comment={comment} focusedOn={focusedOn} disabled={disabled} {...rest}>
            <_Main>
                <_Details>
                    <_Header>
                        <_SwitchContainer>
                            {selected && <CurrentLabel />}
                            {thinkingContent && (
                                <ThinkingBubbleToggleButton
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        onToggleThinking && onToggleThinking();
                                    }}
                                />
                            )}
                            {conversationData && (
                                <Tooltip title="View prompt data" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onToggleConversationMessages) {
                                                onToggleConversationMessages();
                                            } else if (setShowConversationMessages) {
                                                setShowConversationMessages((v) => !v);
                                            }
                                        }}
                                        sx={{ ml: 1, width: 25, height: 25 }}
                                    >
                                        <ModeIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {intents.length > 0 && (
                                <Box
                                    display="flex"
                                    gap={1}
                                    flexWrap="nowrap"
                                    sx={{ overflowX: 'auto', maxWidth: '90%' }}
                                >
                                    {intents.map((intent) => {
                                        const isShort = intents.length > 1;
                                        const labelMaxWidth = isShort ? 90 : 180;
                                        const chipMaxWidth = isShort ? 120 : 240;
                                        return (
                                            <Tooltip key={intent.id} title={intent.name} arrow>
                                                <span>
                                                    <CommentChip
                                                        label={
                                                            <span
                                                                style={{
                                                                    display: 'inline-block',
                                                                    maxWidth: labelMaxWidth,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    verticalAlign: 'bottom',
                                                                }}
                                                            >
                                                                {intent.name}
                                                            </span>
                                                        }
                                                        disabled={isResettingComment}
                                                        onDelete={(event) => {
                                                            event.stopPropagation();
                                                            event.preventDefault();
                                                            resetComment();
                                                        }}
                                                        sx={chipMaxWidth ? { maxWidth: chipMaxWidth } : undefined}
                                                    />
                                                </span>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            )}
                            {showInfo && <InfoButton open={infoOpen} onToggle={setInfoOpen} />}
                            <Switch
                                color={comment.is_public === false ? 'warning' : 'success'}
                                size="small"
                                checked={!comment.is_customer}
                                onChange={() => toggleIsCustomer?.(comment)}
                                onClick={(event) => event.stopPropagation()}
                            />
                        </_SwitchContainer>
                        {(title || comment.agent_username) && (
                            <Box display="flex" alignItems="flex-end" mt={1}>
                                {title && (
                                    <Typography fontWeight={500} flex={1}>
                                        {title}
                                    </Typography>
                                )}
                                {comment.agent_username && (
                                    <Typography ml="auto" variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                        Labeled by: <b>{comment.agent_username}</b>
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </_Header>
                    {formattedJson ? (
                        <Box
                            component="pre"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                p: 1.5,
                                borderRadius: 1,
                                fontSize: '0.8rem',
                                fontFamily: 'monospace',
                                m: 0,
                                overflow: 'auto',
                            }}
                        >
                            {formattedJson}
                        </Box>
                    ) : (
                        html && (comment.body_html ? <root.div>{htmlElem}</root.div> : htmlElem)
                    )}
                    {hasCollapsibleContent && (
                        <Box mt={1}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setIsCollapsed(!isCollapsed)}
                                sx={{
                                    textTransform: 'none',
                                    color: 'text.secondary',
                                    fontSize: '0.875rem',
                                    p: 0,
                                    minWidth: 'auto',
                                }}
                            >
                                {isCollapsed ? 'Show more' : 'Show less'}
                            </Button>
                        </Box>
                    )}
                    {children !== undefined ? children : null}
                    <_Attachments>
                        {attachments.length > 0 && (
                            <_AttachmentsContainer
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    if (!isBodyEmpty) {
                                        setAttachOpen((open) => !open);
                                    }
                                }}
                            >
                                <_AttachmentsLabel variant="subtitle2" color="secondary">
                                    Attachments ({attachments.length})
                                </_AttachmentsLabel>
                                {!isBodyEmpty && <ArrawIcon open={attachOpen} fontSize={0} color="text.secondary" />}
                            </_AttachmentsContainer>
                        )}
                        {comment.is_public === false && <_InternalLabel variant="body2">Internal</_InternalLabel>}
                    </_Attachments>
                </_Details>
                {infoOpen && (
                    <_InfoList>
                        <InfoListItem label="Name" value={comment.from_name} />
                        <InfoListItem label="Email" value={comment.from_email} />
                        <InfoListItem label="Country Code" value={comment.additional_data['country_code']} />
                        <InfoListItem
                            label="Sent"
                            value={format(new Date(comment.inquiry_date), 'HH:mm, MMM dd, yyyy')}
                        />
                        <InfoListItem label="Type of issue" value={comment.additional_data['issue_type']} />
                    </_InfoList>
                )}
            </_Main>
            {attachOpen && <Attachments attachments={attachments} />}
        </BubbleContainer>
    );
    return disabled ? (
        <Tooltip title="This comment is read-only. Please move to the latest comment." placement="right">
            {bubbleContent}
        </Tooltip>
    ) : (
        bubbleContent
    );
};

export default CommentBubble;
